import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Button,
  Alert,
  Box,
  Typography,
  Chip,
  CircularProgress,
} from '@mui/material';
import { Warning as WarningIcon } from '@mui/icons-material';
import type { Content } from '../../types/content.types';

interface DeleteContentDialogProps {
  open: boolean;
  content: Content | null;
  onClose: () => void;
  onConfirm: () => Promise<void>;
}

const DeleteContentDialog: React.FC<DeleteContentDialogProps> = ({
  open,
  content,
  onClose,
  onConfirm,
}) => {
  const [deleting, setDeleting] = useState(false);

  const handleConfirm = async () => {
    setDeleting(true);
    try {
      await onConfirm();
      onClose();
    } catch (error) {
      console.error('Error deleting content:', error);
      // Error handling is done by parent component
    } finally {
      setDeleting(false);
    }
  };

  const handleClose = () => {
    if (deleting) return;
    onClose();
  };

  // Helper function to format file size
  const formatFileSize = (bytes?: number | string): string => {
    const numBytes = Number(bytes);
    if (!numBytes || isNaN(numBytes)) return '';
    const units = ['B', 'KB', 'MB', 'GB'];
    let size = numBytes;
    let unitIndex = 0;
    while (size >= 1024 && unitIndex < units.length - 1) {
      size /= 1024;
      unitIndex++;
    }
    return `${size.toFixed(1)} ${units[unitIndex]}`;
  };

  if (!content) return null;

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="sm" 
      fullWidth
      PaperProps={{
        sx: {
          '& .MuiDialogTitle-root': {
            backgroundColor: 'error.light',
            color: 'error.contrastText',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }
        }
      }}
    >
      <DialogTitle>
        <WarningIcon />
        Delete Content
      </DialogTitle>
      
      <DialogContent sx={{ pt: 2 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          <strong>This action cannot be undone!</strong>
        </Alert>

        <DialogContentText sx={{ mb: 2 }}>
          Are you sure you want to permanently delete this content? This will remove:
        </DialogContentText>

        <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
            {content.title}
          </Typography>
          
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {content.description}
          </Typography>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
            {content.contentType && (
              <Chip label={content.contentType} size="small" />
            )}
            {content.fileSize && (
              <Chip label={formatFileSize(content.fileSize)} size="small" variant="outlined" />
            )}
          </Box>
        </Box>

        <Box component="ul" sx={{ pl: 2, mb: 2 }}>
          <Typography component="li" variant="body2" color="error.main">
            The content file and all its versions
          </Typography>
          <Typography component="li" variant="body2" color="error.main">
            All metadata and tags associated with this content
          </Typography>
          {content.reviewCount && content.reviewCount > 0 && (
            <Typography component="li" variant="body2" color="error.main">
              {content.reviewCount} user rating{content.reviewCount !== 1 ? 's' : ''} and reviews
            </Typography>
          )}
          <Typography component="li" variant="body2" color="error.main">
            View history ({content.viewCount || 0} views) and download history ({content.downloadCount || 0} downloads)
          </Typography>
          <Typography component="li" variant="body2" color="error.main">
            Any bookmarks users have created for this content
          </Typography>
        </Box>

        <DialogContentText>
          Type the content title <strong>"{content.title}"</strong> to confirm deletion:
        </DialogContentText>
        
        {/* Note: For enhanced security, you could add a text input here requiring 
             the user to type the content title to confirm deletion */}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={deleting}>
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          color="error"
          variant="contained"
          disabled={deleting}
          startIcon={deleting ? <CircularProgress size={18} /> : undefined}
        >
          {deleting ? 'Deleting...' : 'Delete Content'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeleteContentDialog;